import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'flowbite-react';
import { Form, Formik } from 'formik';
import { Client } from '../../models';
import { Button, ClientSelectField, Icon } from '..';

export type TrackClientPromptModalProps = {
    show: boolean;
    onClose?: () => void;
    onConfirm: (client: Client) => void;
    loading?: boolean;
};

/** Keep dropdown results visible: body scrolls instead of clipping absolute menus. */
const modalTheme = {
    content: {
        base: 'relative w-full p-4 h-auto',
        inner: 'relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col min-h-0 h-auto max-h-[90vh] w-full overflow-hidden',
    },
    body: {
        base: 'p-6 flex-1 overflow-y-auto min-h-0',
        popup: 'pt-0',
    },
};

function TrackClientPromptModal(props: TrackClientPromptModalProps) {
    const { show, onClose, onConfirm, loading } = props;
    const { t } = useTranslation();
    const [clientError, setClientError] = useState('');

    useEffect(() => {
        if (show)
            setClientError('');
    }, [show]);

    if (!show)
        return null;

    return (
        <Modal show={show} size="md" dismissible onClose={onClose} theme={modalTheme}>
            <Modal.Header>
                <div className="flex items-center gap-x-2 min-w-0">
                    <Icon type="timer" size={5} className="shrink-0" />
                    <span className="truncate">{t('tracks.choose_client_title')}</span>
                </div>
            </Modal.Header>
            <Formik
                initialValues={{ client: undefined as Client | undefined }}
                enableReinitialize
                onSubmit={(values) => {
                    const client = values.client;
                    if (!client?._id) {
                        setClientError(t('tracks.select_client') || '');
                        return;
                    }
                    setClientError('');
                    onConfirm(client);
                }}
            >
                {({ setFieldValue }) => (
                    <Form
                        className="flex min-h-0 flex-1 flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <Modal.Body>
                            <div className="flex flex-col gap-y-3">
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                    {t('tracks.choose_client_hint')}
                                </p>
                                <div className="flex flex-col gap-y-1">
                                    <label className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                        {t('tracks.client')}
                                    </label>
                                    <ClientSelectField
                                        name="client"
                                        placeholder={t('tracks.select_client') || ''}
                                        autoFocus
                                        resultsInFlow
                                        onChange={(client?: Client) => {
                                            setFieldValue('client', client || null, false);
                                            setClientError('');
                                        }}
                                    />
                                    {clientError && (
                                        <p className="text-xs text-red-600">{clientError}</p>
                                    )}
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="flex w-full justify-end gap-3">
                                <Button
                                    type="button"
                                    color="default"
                                    title={t('common.cancel') || ''}
                                    onClick={onClose}
                                />
                                <Button
                                    type="submit"
                                    color="primary"
                                    icon="start"
                                    title={t('client_view.start_track') || t('tracks.start') || ''}
                                    loading={!!loading}
                                />
                            </div>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
}

export default TrackClientPromptModal;
